import { type PropsWithChildren, forwardRef, useEffect } from "react";

import type { PaginationProps } from "./Pagination.types";
import { valueOrNothing } from "$/lib/utils/functions/util.functions";
import ChevronLineIcon from "$/lib/icons/ChevronLineIcon";
import ChevronIcon from "$/lib/icons/ChevronIcon";
import { PaginationButton } from "../../atoms";

function generatePageNumbers(currentPage: number, totalNumberOfPages: number) {
  if (totalNumberOfPages <= 3) {
    return Array.from({ length: totalNumberOfPages }, (_, index) => index + 1);
  }
  if (currentPage === totalNumberOfPages) {
    return [totalNumberOfPages - 2, totalNumberOfPages - 1, totalNumberOfPages];
  }

  if (currentPage === 1) {
    return [currentPage, currentPage + 1, currentPage + 2];
  }

  return [currentPage - 1, currentPage, currentPage + 1];
}

const Pagination = forwardRef<
  HTMLDivElement,
  PropsWithChildren<PaginationProps>
>(
  (
    {
      isFilled = true,
      isDisabled = false,
      totalNumberOfPages,
      currentPage,
      onNextClick,
      onPreviousClick,
      onSetPageNumber,
      isPendingData,
    },
    forwardedRef,
  ) => {
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalNumberOfPages;

    useEffect(() => {
      if (currentPage > totalNumberOfPages && !isPendingData) {
        onSetPageNumber(currentPage - 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, totalNumberOfPages, isPendingData]);

    return (
      <div
        ref={forwardedRef}
        className="mt-2 flex h-10 items-center justify-center gap-12 px-4 md:justify-end"
      >
        <div className="flex items-center justify-end gap-4">
          <PaginationButton
            buttonType="utility"
            isFilled={isFilled}
            currentPage={currentPage}
            disabled={currentPage <= 2 || isPendingData || isDisabled}
            onClick={valueOrNothing(!isFirstPage, () => onSetPageNumber(1))}
          >
            <ChevronLineIcon width={18} height={18} className="rotate-180" />
          </PaginationButton>
          <PaginationButton
            buttonType="utility"
            isFilled={isFilled}
            currentPage={currentPage}
            disabled={currentPage === 1 || isPendingData || isDisabled}
            onClick={valueOrNothing(!isFirstPage, onPreviousClick)}
          >
            <ChevronIcon width={18} height={18} className="rotate-180" />
          </PaginationButton>

          {/* number */}
          {generatePageNumbers(currentPage, totalNumberOfPages).map(
            (pageNumber) => {
              const isCurrentPage = currentPage === pageNumber;

              return (
                <PaginationButton
                  buttonType="page"
                  key={pageNumber}
                  disabled={isPendingData || isDisabled}
                  onClick={valueOrNothing(!isCurrentPage, () =>
                    onSetPageNumber(pageNumber),
                  )}
                  isFilled={isFilled}
                  currentPage={currentPage}
                  pageNumber={pageNumber}
                >
                  {pageNumber}
                </PaginationButton>
              );
            },
          )}

          {/* next arrow */}
          <PaginationButton
            buttonType="utility"
            disabled={
              currentPage === totalNumberOfPages || isPendingData || isDisabled
            }
            isFilled={isFilled}
            onClick={isLastPage ? undefined : onNextClick}
            currentPage={currentPage}
          >
            <ChevronIcon width={18} height={18} />
          </PaginationButton>
          <PaginationButton
            buttonType="utility"
            isFilled={isFilled}
            disabled={
              totalNumberOfPages === currentPage || isPendingData || isDisabled
            }
            onClick={() => onSetPageNumber(totalNumberOfPages)}
            currentPage={currentPage}
          >
            <ChevronLineIcon width={18} height={18} />
          </PaginationButton>
        </div>
      </div>
    );
  },
);

Pagination.displayName = "Pagination";

export default Pagination;
